import { api } from './api'
import { getAll } from './pagination'

export interface DashboardWidget {
  id: number
  name: string
  title: string
  description: string
  widget_type: 'chart' | 'metric' | 'table' | 'gauge' | 'progress' | 'trend' | 'heatmap' | 'pie' | 'bar' | 'line' | 'scatter' | 'map' | 'text' | 'alert' | 'other'
  size: 'small' | 'medium' | 'large' | 'wide' | 'tall' | 'full'
  data_source: string
  data_config: any
  refresh_interval: number
  position_x: number
  position_y: number
  width: number
  height: number
  is_public: boolean
  allowed_roles: string[]
  allowed_users_names: string[]
  is_active: boolean
  is_featured: boolean
  tags: string[]
  notes: string
  created_by: number
  created_by_name: string
  created_at: string
  updated_at: string
}

export interface DashboardLayout {
  id: number
  name: string
  title: string
  description: string
  layout_type: 'admin' | 'teacher' | 'parent' | 'student' | 'staff' | 'custom'
  layout_config: any
  is_default: boolean
  is_public: boolean
  allowed_roles: string[]
  allowed_users_names: string[]
  is_active: boolean
  widgets_count: number
  notes: string
  created_by: number
  created_by_name: string
  created_at: string
  updated_at: string
}

export interface AnalyticsMetric {
  id: number
  name: string
  title: string
  description: string
  metric_type: 'count' | 'percentage' | 'average' | 'sum' | 'min' | 'max' | 'trend' | 'ratio' | 'rate' | 'other'
  category: 'academic' | 'attendance' | 'behavior' | 'health' | 'extracurricular' | 'portfolio' | 'system' | 'user' | 'financial' | 'other'
  data_source: string
  calculation_method: string
  aggregation_period: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
  unit: string
  decimal_places: number
  color_code: string
  warning_threshold?: number
  critical_threshold?: number
  target_value?: number
  is_active: boolean
  is_public: boolean
  tags: string[]
  notes: string
  created_by: number
  created_by_name: string
  latest_value?: {
    value: number
    date: string
    timestamp: string
  }
  trend_direction: 'up' | 'down' | 'stable' | 'unknown'
  created_at: string
  updated_at: string
}

export interface MetricValue {
  id: number
  metric: number
  metric_title: string
  metric_unit: string
  value: number
  date: string
  timestamp: string
  context_data: any
  calculated_by: string
  calculation_method: string
}

export interface StudentInsight {
  id: number
  student: number
  student_name: string
  student_id: string
  student_class: string
  title: string
  description: string
  insight_type: 'academic' | 'attendance' | 'behavior' | 'health' | 'extracurricular' | 'portfolio' | 'social' | 'emotional' | 'predictive' | 'recommendation' | 'other'
  priority: 'low' | 'medium' | 'high' | 'critical'
  data_points: any[]
  analysis_method: string
  confidence_score: number
  recommendations: string[]
  suggested_actions: string[]
  follow_up_required: boolean
  is_acknowledged: boolean
  acknowledged_by?: number
  acknowledged_by_name?: string
  acknowledged_at?: string
  insight_date: string
  valid_until?: string
  tags: string[]
  notes: string
  created_at: string
  updated_at: string
}

export interface SystemAlert {
  id: number
  title: string
  description: string
  alert_type: 'performance' | 'security' | 'data' | 'system' | 'maintenance' | 'capacity' | 'integration' | 'user' | 'other'
  severity: 'info' | 'warning' | 'error' | 'critical'
  source: string
  data: any
  is_resolved: boolean
  resolved_by?: number
  resolved_by_name?: string
  resolved_at?: string
  resolution_notes: string
  alert_date: string
  expires_at?: string
  tags: string[]
  notes: string
  created_by: number
  created_by_name: string
}

export interface AnalyticsReport {
  id: number
  name: string
  title: string
  description: string
  report_type: 'dashboard' | 'student' | 'class' | 'academic' | 'attendance' | 'behavior' | 'health' | 'extracurricular' | 'portfolio' | 'system' | 'custom'
  date_from?: string
  date_to?: string
  filters: any
  file_path: string
  file_size: number
  generated_at: string
  generated_by?: number
  generated_by_name?: string
  summary_data: any
  detailed_data: any
  is_public: boolean
  is_scheduled: boolean
}

export interface DashboardSummary {
  total_widgets: number
  active_widgets: number
  total_layouts: number
  active_layouts: number
  total_metrics: number
  active_metrics: number
  total_insights: number
  unacknowledged_insights: number
  total_alerts: number
  unresolved_alerts: number
}

export interface SystemPerformance {
  total_users: number
  active_users: number
  total_students: number
  active_students: number
  total_teachers: number
  active_teachers: number
  system_uptime: number | null
  response_time: number | null
  error_rate: number | null
  storage_usage: number | null
  memory_usage: number | null
  cpu_usage: number | null
}

export interface AcademicPerformance {
  total_students: number
  average_grade: number
  pass_rate: number
  fail_rate: number
  improvement_rate: number
  top_performers: number
  struggling_students: number
  grade_distribution: any
}

export interface AttendanceAnalytics {
  total_students: number
  average_attendance: number
  attendance_rate: number
  absent_students: number
  late_students: number
  perfect_attendance: number
  attendance_trends: any[]
}

export interface BehaviorAnalytics {
  total_incidents: number
  resolved_incidents: number
  pending_incidents: number
  incident_rate: number
  most_common_incidents: any[]
  behavior_trends: any[]
  intervention_success_rate: number
}

export interface HealthAnalytics {
  total_health_records: number
  active_health_alerts: number
  resolved_health_alerts: number
  emergency_contacts: number
  health_trends: any[]
  common_health_issues: any[]
}

export interface ExtracurricularAnalytics {
  total_activities: number
  active_activities: number
  total_participants: number
  participation_rate: number
  total_teams: number
  active_teams: number
  total_achievements: number
  activity_trends: any[]
}

export interface PortfolioAnalytics {
  total_portfolio_items: number
  total_achievements: number
  total_skills: number
  total_goals: number
  completed_goals: number
  verified_items: number
  portfolio_trends: any[]
}

export interface ComprehensiveAnalytics {
  system_performance: SystemPerformance
  academic_performance: AcademicPerformance
  attendance_analytics: AttendanceAnalytics
  behavior_analytics: BehaviorAnalytics
  health_analytics: HealthAnalytics
  extracurricular_analytics: ExtracurricularAnalytics
  portfolio_analytics: PortfolioAnalytics
  dashboard_summary: DashboardSummary
}

/** Figures over a window of whole months (see backend apps/analytics/live.py). Numbers
 *  arrive as numbers or decimal strings; `null` means there was nothing to measure. */
export interface AnalyticsWindow {
  start: string
  end: string
  months: number
  previous_start: string
  previous_end: string
}

export interface AnalyticsSummary {
  window: AnalyticsWindow
  total_students: number
  total_teachers: number
  total_classes: number
  results_recorded: number
  average_performance: number | null
  previous_average_performance: number | null
  pass_rate: number | null
  attendance_records: number
  average_attendance: number | null
  previous_average_attendance: number | null
  money_received: string | number
  previous_money_received: string | number
  outstanding_fees: string | number
  collection_rate: number | null
  behavior_incidents: number
  previous_behavior_incidents: number
}

export interface PerformanceTrend {
  [key: string]: unknown
  period: string
  academic_performance: number | null
  results: number
  attendance_rate: number | null
  attendance_records: number
  money_received: string | number
  incidents: number
}

export interface ClassPerformance {
  class_id: number
  class_name: string
  total_students: number
  results: number
  learners_assessed: number
  average_score: number | null
  pass_rate: number | null
  attendance_rate: number | null
  attendance_records: number
}

export interface SubjectPerformance {
  subject_name: string
  results: number
  total_students: number
  average_score: number
  previous_average_score: number | null
  pass_rate: number | null
  grade_distribution: Record<'A' | 'B' | 'C' | 'D' | 'F', number>
}

export interface RevenueAnalysis {
  [key: string]: unknown
  period: string
  fees: string | number
  transport: string | number
  boarding: string | number
  cafeteria: string | number
  uniforms: string | number
  other: string | number
  total: string | number
}

export interface AttendancePattern {
  day_of_week: string
  attendance_rate: number
  total_days: number
  days_present: number
  days_absent: number
}

// Indexed so recharts accepts these rows directly as chart data.
export interface BehaviorAnalysisEntry {
  [key: string]: unknown
  incident_type: string
  count: number
  percentage: number
  trend: string
}

export interface PredictiveInsight {
  prediction_type: string
  target: string
  predicted_value: number
  confidence: number
  recommendation: string
  timeframe: string
}

export interface BulkInsightAcknowledgmentData {
  insight_ids: number[]
  acknowledgment_notes?: string
}

export interface BulkAlertResolutionData {
  alert_ids: number[]
  resolution_notes?: string
}

class AnalyticsService {
  // Dashboard Widgets
  async getDashboardWidgets(params?: {
    widget_type?: string
    size?: string
    is_active?: boolean
    is_public?: boolean
    is_featured?: boolean
    search?: string
    ordering?: string
  }): Promise<DashboardWidget[]> {
    return getAll('/analytics/dashboard/widgets/', params)
  }

  async getDashboardWidget(id: number): Promise<DashboardWidget> {
    const response = await api.get(`/analytics/dashboard/widgets/${id}/`)
    return response.data
  }

  async createDashboardWidget(data: Partial<DashboardWidget>): Promise<DashboardWidget> {
    const response = await api.post('/analytics/dashboard/widgets/', data)
    return response.data
  }

  async updateDashboardWidget(id: number, data: Partial<DashboardWidget>): Promise<DashboardWidget> {
    const response = await api.patch(`/analytics/dashboard/widgets/${id}/`, data)
    return response.data
  }

  async deleteDashboardWidget(id: number): Promise<void> {
    await api.delete(`/analytics/dashboard/widgets/${id}/`)
  }

  // Dashboard Layouts
  async getDashboardLayouts(params?: {
    layout_type?: string
    is_active?: boolean
    is_public?: boolean
    is_default?: boolean
    search?: string
    ordering?: string
  }): Promise<DashboardLayout[]> {
    return getAll('/analytics/dashboard/layouts/', params)
  }

  async getDashboardLayout(id: number): Promise<DashboardLayout> {
    const response = await api.get(`/analytics/dashboard/layouts/${id}/`)
    return response.data
  }

  async createDashboardLayout(data: Partial<DashboardLayout>): Promise<DashboardLayout> {
    const response = await api.post('/analytics/dashboard/layouts/', data)
    return response.data
  }

  async updateDashboardLayout(id: number, data: Partial<DashboardLayout>): Promise<DashboardLayout> {
    const response = await api.patch(`/analytics/dashboard/layouts/${id}/`, data)
    return response.data
  }

  async deleteDashboardLayout(id: number): Promise<void> {
    await api.delete(`/analytics/dashboard/layouts/${id}/`)
  }

  // Analytics Metrics
  async getAnalyticsMetrics(params?: {
    metric_type?: string
    category?: string
    is_active?: boolean
    is_public?: boolean
    search?: string
    ordering?: string
  }): Promise<AnalyticsMetric[]> {
    return getAll('/analytics/dashboard/metrics/', params)
  }

  async getAnalyticsMetric(id: number): Promise<AnalyticsMetric> {
    const response = await api.get(`/analytics/dashboard/metrics/${id}/`)
    return response.data
  }

  async createAnalyticsMetric(data: Partial<AnalyticsMetric>): Promise<AnalyticsMetric> {
    const response = await api.post('/analytics/dashboard/metrics/', data)
    return response.data
  }

  async updateAnalyticsMetric(id: number, data: Partial<AnalyticsMetric>): Promise<AnalyticsMetric> {
    const response = await api.patch(`/analytics/dashboard/metrics/${id}/`, data)
    return response.data
  }

  async deleteAnalyticsMetric(id: number): Promise<void> {
    await api.delete(`/analytics/dashboard/metrics/${id}/`)
  }

  // Metric Values
  async getMetricValues(params?: {
    metric?: number
    date?: string
    ordering?: string
  }): Promise<MetricValue[]> {
    return getAll('/analytics/dashboard/metric-values/', params)
  }

  async createMetricValue(data: Partial<MetricValue>): Promise<MetricValue> {
    const response = await api.post('/analytics/dashboard/metric-values/', data)
    return response.data
  }

  // Student Insights
  async getStudentInsights(params?: {
    student?: number
    insight_type?: string
    priority?: string
    is_acknowledged?: boolean
    follow_up_required?: boolean
    search?: string
    ordering?: string
  }): Promise<StudentInsight[]> {
    return getAll('/analytics/dashboard/insights/', params)
  }

  async getStudentInsight(id: number): Promise<StudentInsight> {
    const response = await api.get(`/analytics/dashboard/insights/${id}/`)
    return response.data
  }

  async createStudentInsight(data: Partial<StudentInsight>): Promise<StudentInsight> {
    const response = await api.post('/analytics/dashboard/insights/', data)
    return response.data
  }

  async updateStudentInsight(id: number, data: Partial<StudentInsight>): Promise<StudentInsight> {
    const response = await api.patch(`/analytics/dashboard/insights/${id}/`, data)
    return response.data
  }

  async deleteStudentInsight(id: number): Promise<void> {
    await api.delete(`/analytics/dashboard/insights/${id}/`)
  }

  // System Alerts
  async getSystemAlerts(params?: {
    alert_type?: string
    severity?: string
    is_resolved?: boolean
    source?: string
    search?: string
    ordering?: string
  }): Promise<SystemAlert[]> {
    return getAll('/analytics/dashboard/alerts/', params)
  }

  async getSystemAlert(id: number): Promise<SystemAlert> {
    const response = await api.get(`/analytics/dashboard/alerts/${id}/`)
    return response.data
  }

  async createSystemAlert(data: Partial<SystemAlert>): Promise<SystemAlert> {
    const response = await api.post('/analytics/dashboard/alerts/', data)
    return response.data
  }

  async updateSystemAlert(id: number, data: Partial<SystemAlert>): Promise<SystemAlert> {
    const response = await api.patch(`/analytics/dashboard/alerts/${id}/`, data)
    return response.data
  }

  async deleteSystemAlert(id: number): Promise<void> {
    await api.delete(`/analytics/dashboard/alerts/${id}/`)
  }

  // Analytics Reports
  async getAnalyticsReports(params?: {
    report_type?: string
    is_public?: boolean
    is_scheduled?: boolean
    search?: string
    ordering?: string
  }): Promise<AnalyticsReport[]> {
    return getAll('/analytics/dashboard/reports/', params)
  }

  async createAnalyticsReport(data: Partial<AnalyticsReport>): Promise<AnalyticsReport> {
    const response = await api.post('/analytics/dashboard/reports/', data)
    return response.data
  }

  // Analytics Endpoints
  async getDashboardSummary(): Promise<DashboardSummary> {
    const response = await api.get('/analytics/dashboard/summary/')
    return response.data
  }

  async getSystemPerformance(): Promise<SystemPerformance> {
    const response = await api.get('/analytics/dashboard/system-performance/')
    return response.data
  }

  async getAcademicPerformance(): Promise<AcademicPerformance> {
    const response = await api.get('/analytics/dashboard/academic-performance/')
    return response.data
  }

  async getAttendanceAnalytics(): Promise<AttendanceAnalytics> {
    const response = await api.get('/analytics/dashboard/attendance-analytics/')
    return response.data
  }

  async getBehaviorAnalytics(): Promise<BehaviorAnalytics> {
    const response = await api.get('/analytics/dashboard/behavior-analytics/')
    return response.data
  }

  async getHealthAnalytics(): Promise<HealthAnalytics> {
    const response = await api.get('/analytics/dashboard/health-analytics/')
    return response.data
  }

  async getExtracurricularAnalytics(): Promise<ExtracurricularAnalytics> {
    const response = await api.get('/analytics/dashboard/extracurricular-analytics/')
    return response.data
  }

  async getPortfolioAnalytics(): Promise<PortfolioAnalytics> {
    const response = await api.get('/analytics/dashboard/portfolio-analytics/')
    return response.data
  }

  async getComprehensiveAnalytics(): Promise<ComprehensiveAnalytics> {
    const response = await api.get('/analytics/dashboard/comprehensive-analytics/')
    return response.data
  }

  // Analytics Reporting Endpoints
  async getSummary(months: number): Promise<AnalyticsSummary> {
    const response = await api.get('/analytics/reports/summary/', { params: { months } })
    return response.data
  }

  async getPerformanceTrends(months: number): Promise<PerformanceTrend[]> {
    const response = await api.get('/analytics/reports/performance_trends/', {
      params: { months },
    })
    return response.data
  }

  async getClassPerformance(months: number): Promise<ClassPerformance[]> {
    const response = await api.get('/analytics/reports/class_performance/', { params: { months } })
    return response.data
  }

  async getSubjectPerformance(months: number): Promise<SubjectPerformance[]> {
    const response = await api.get('/analytics/reports/subject_performance/', { params: { months } })
    return response.data
  }

  async getRevenueAnalysis(months: number): Promise<RevenueAnalysis[]> {
    const response = await api.get('/analytics/reports/revenue_analysis/', {
      params: { months },
    })
    return response.data
  }

  async getAttendancePatterns(months: number): Promise<AttendancePattern[]> {
    const response = await api.get('/analytics/reports/attendance_patterns/', { params: { months } })
    return response.data
  }

  async getBehaviorAnalysis(months: number): Promise<BehaviorAnalysisEntry[]> {
    const response = await api.get('/analytics/reports/behavior_analysis/', { params: { months } })
    return response.data
  }

  async getPredictiveInsights(): Promise<PredictiveInsight[]> {
    const response = await api.get('/analytics/reports/predictive_insights/')
    return response.data
  }

  // Bulk Operations
  async bulkAcknowledgeInsights(data: BulkInsightAcknowledgmentData): Promise<{ message: string; acknowledged_count: number }> {
    const response = await api.post('/analytics/dashboard/insights/bulk-acknowledge/', data)
    return response.data
  }

  async bulkResolveAlerts(data: BulkAlertResolutionData): Promise<{ message: string; resolved_count: number }> {
    const response = await api.post('/analytics/dashboard/alerts/bulk-resolve/', data)
    return response.data
  }
}

export const analyticsService = new AnalyticsService()
